import { useState, useEffect } from 'react';
import './candidateWorkExperience.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faPen, faMinusCircle, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { Col, Container, Row, Modal, Button, Form } from 'react-bootstrap';
import ExperienceIcon from '../../../../assets/images/Ic_work position.png';
import CandidateJobRoleDetails from './candidateJobRoleDetails';
import { CandidatePostLoginService } from '../../../../services/candidatePostLoginService';
import { MasterDataService } from '../../../../services/masterDataService';
import CloseIcon from '../../../../assets/images/Ic_close.png';
import ReactDatePicker from 'react-datepicker';

const CandidateWorkExperinceInfo = ({ jobRoles = [], careerGaps = [], candidateId, candidateData, fetchCandidateData, refreshPage }) => {
  const [showAll, setShowAll] = useState(false);
  const [editedJobRoles, setEditedJobRoles] = useState([]);
  const [editingIndexes, setEditingIndexes] = useState([]);
  const [modalIndex, setModalIndex] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [modalFormData, setModalFormData] = useState(null);
  const [savingJobRoles, setSavingJobRoles] = useState(false);
  const [cities, setCities] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [modalMeta, setModalMeta] = useState({ isCareerGap: false, isCurrent: false, isNew: false, });
  const [removedModals, setRemovedModals] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  const [industrySectors, setIndustrySectors] = useState([]);
  const [modalSubcategoriesMap, setModalSubcategoriesMap] = useState({});
  const [functions, setFunctions] = useState([]);
  const [modalFunctionSubcategoriesMap, setModalFunctionSubcategoriesMap] = useState({});

  const validateWorkField = (field, value, related = {}) => {
    const currentYear = new Date().getFullYear();
    const today = new Date();
    const trimmed = typeof value === 'string' ? value.trim() : value;

    if (field === 'startDate') {
      const start = new Date(trimmed);
      if (!trimmed) return '';
      if (start > today) return 'Start date cannot be in the future';
      if (related.endDate && start > new Date(related.endDate)) return 'Start date cannot be after end date';
    }

    if (field === 'endDate') {
      const end = new Date(trimmed);
      if (!trimmed) return '';
      if (related.startDate && end < new Date(related.startDate)) return 'End date cannot be before start date';
    }

    if (field === 'reasons') {
      if (trimmed.length > 250) return 'Reason cannot exceed 250 characters';
    }

    if (['designation', 'orgName', 'industry'].includes(field)) {
      if (!trimmed || trimmed.length < 2 || trimmed.length > 100)
        return `${field} must be between 2 and 100 characters`;
    }
    if (field === 'previousDesignation') {
      if (!trimmed || trimmed.length < 2 || trimmed.length > 100)
        return 'Previous designation must be between 2 and 100 characters';
    }


    if (field === 'topThreeResponsibilities' || field === 'topThreeAchievements') {
      const wordCount = trimmed.split(/\s+/).filter(Boolean).length;
      if (wordCount > 250) return `${field} cannot exceed 250 words`;
    }

    if (field === 'promotionYr') {
      if (!trimmed) return 'Promotion year is required';
      if (!/^\d{4}$/.test(trimmed)) return 'Enter a valid year (e.g., 2022)';
      const year = parseInt(trimmed);
      const currentYear = new Date().getFullYear();
      if (year < 1900 || year > currentYear) return 'Year out of valid range';
    }

    return '';
  };


  const validateJobForm = (data, isCurrentRole = false) => {
    const errors = [];

    if (!data.designation?.trim()) errors.push('Designation');
    if (!data.orgName?.trim()) errors.push('Organization Name');
    if (!data.industry?.trim()) errors.push('Industry');


    if (data.hasPromotions === 'YES') {
      const validPromotions = (data.promotions || []).filter(p => p.previousDesignation?.trim() && p.promotionYr);
      if (validPromotions.length === 0) {
        errors.push('At least one Promotion (Previous Designation and Year)');
      }
    }

    return errors;
  };

  const validateCareerGapForm = (data) => {
    const errors = [];
    if (!data.startDate) errors.push('Gap Start Date');
    if (!data.endDate) errors.push('Gap End Date');

    return errors;
  };

  const isEditMode = (() => {
    if (modalFormData) return modalFormData._isNew !== true;
    return modalMeta.isNew === false;
  })();


  useEffect(() => {
    MasterDataService.getAllCities()
      .then((res) => setCities(res.data))
      .catch((err) => console.error('Failed to load cities:', err));
  }, []);


  useEffect(() => {
    const loadIndustries = async () => {
      try {
        const res = await MasterDataService.getAllIndustrySectors();
        const raw = res?.data || [];
        const normalized = raw.map((ind) => {
          if (typeof ind === 'string') return { id: null, industrySector: ind };
          return {
            id: ind.id ?? null,
            industrySector: ind.industrySector || ind.industrySectorName || ind.name || '',
          };
        });
        setIndustrySectors(normalized);
      } catch (err) {
        console.error('Failed loading industry sectors', err);
        setIndustrySectors([]);
      }
    };
    loadIndustries();

    const loadFunctions = async () => {
      try {
        const r = await MasterDataService.getAllFunctions();
        const rawf = r?.data || [];
        const normalized = rawf.map((f) => {
          if (typeof f === 'string') return { id: null, function: f };
          return { id: f.id ?? null, function: f.function || f.name || '' };
        });
        setFunctions(normalized);
      } catch (err) {
        console.error('Failed loading functions', err);
        setFunctions([]);
      }
    };
    loadFunctions();
  }, []);


  useEffect(() => {
    const safeCareerGaps = Array.isArray(careerGaps) ? careerGaps : [];
    const safeJobRoles = Array.isArray(jobRoles) ? jobRoles : [];

    const extended = [
      ...safeJobRoles,
      ...(safeCareerGaps.length > 0 ? safeCareerGaps.map(gap => ({ isCareerGap: true, ...gap })) : [])
    ];

    setEditedJobRoles(extended);
  }, [jobRoles, careerGaps]);


  const handleFieldChange = (index, updatedFields) => {
    setEditedJobRoles(prev => {
      const newRoles = [...prev];
      newRoles[index] = { ...newRoles[index], ...updatedFields };
      return newRoles;
    });
  };

  const fetchSubcategoriesForKey = async (industryId, key) => {
    if (!industryId) {
      setModalSubcategoriesMap(prev => ({ ...prev, [key]: [] }));
      return;
    }
    try {
      const res = await MasterDataService.getIndustrySubcategories(industryId);
      setModalSubcategoriesMap(prev => ({ ...prev, [key]: res.data || [] }));
    } catch (err) {
      console.error('Failed to load subcategories for key', key, err);
      setModalSubcategoriesMap(prev => ({ ...prev, [key]: [] }));
    }
  };

  const fetchFunctionSubcategoriesForKey = async (functionId, key) => {
    if (!functionId) {
      setModalFunctionSubcategoriesMap(prev => ({ ...prev, [key]: [] }));
      return;
    }
    try {
      const res = await MasterDataService.getFunctionSubcategories(functionId);
      setModalFunctionSubcategoriesMap(prev => ({ ...prev, [key]: res.data || [] }));
    } catch (err) {
      console.error('Failed to load function subcategories for key', key, err);
      setModalFunctionSubcategoriesMap(prev => ({ ...prev, [key]: [] }));
    }
  };


  const handleSave = async (updatedRoles = editedJobRoles) => {
    try {

      if (modalIndex === 0 && modalFormData?.currentRole) {
        const wasNew = modalFormData._isNew;

        if (wasNew) {
          const oldCurrent = { ...editedJobRoles[0], currentRole: false };
          updatedRoles = [modalFormData, oldCurrent, ...editedJobRoles.slice(1)];
        } else {
          updatedRoles = [modalFormData, ...editedJobRoles.slice(1)];
        }


      } else if (modalIndex >= editedJobRoles.length && modalFormData) {
        updatedRoles = [...editedJobRoles, modalFormData];
      } else if (modalFormData && modalIndex > 0) {
        updatedRoles = [...editedJobRoles];
        updatedRoles[modalIndex] = modalFormData;
      }

      const currentRole = updatedRoles[0] && !updatedRoles[0].isCareerGap
        ? {
          designation: updatedRoles[0]?.designation || '',
          orgName: updatedRoles[0]?.orgName || '',
          startDate: updatedRoles[0]?.startDate ? new Date(updatedRoles[0].startDate).toISOString() : null,
          endDate: updatedRoles[0]?.endDate ? new Date(updatedRoles[0].endDate).toISOString() : null,
          industry: updatedRoles[0]?.industry || '',
          industrySubcategory: updatedRoles[0]?.industrySubcategory || '',
          function: updatedRoles[0]?.function || '',
          functionSubcategory: updatedRoles[0]?.functionSubcategory || '',
          location: updatedRoles[0]?.location || '',
          noticePeriod: updatedRoles[0]?.noticePeriod ? Number(updatedRoles[0].noticePeriod) : 0,
          topThreeResponsibilities: updatedRoles[0]?.topThreeResponsibilities || '',
          topThreeAchievements: updatedRoles[0]?.topThreeAchievements || '',
          reportingTo: updatedRoles[0]?.reportingTo || '',
          noOfReporters: updatedRoles[0]?.noOfReporters || 0,
          teamSize: updatedRoles[0]?.teamSize || 0,
          hasPromotions:
            Array.isArray(updatedRoles[0]?.promotions) &&
              updatedRoles[0].promotions.some(p => p.previousDesignation || p.promotionYr)
              ? 'YES'
              : 'NO',
          promotions: updatedRoles[0]?.promotions || [],
          currentRole: true,
        }
        : null;

      const previousJobs = updatedRoles.slice(1).filter(role => !role.isCareerGap).map(job => ({
        designation: job?.designation || '',
        orgName: job?.orgName || '',
        startDate: job?.startDate ? new Date(job.startDate).toISOString() : null,
        endDate: job?.endDate ? new Date(job.endDate).toISOString() : null,
        industry: job?.industry || '',
        industrySubcategory: job?.industrySubcategory || '',
        function: job?.function || '',
        functionSubcategory: job?.functionSubcategory || '',
        location: job?.location || '',
        noticePeriod: job?.noticePeriod ? Number(job.noticePeriod) : 0,
        topThreeResponsibilities: job?.topThreeResponsibilities || '',
        topThreeAchievements: job?.topThreeAchievements || '',
        reportingTo: job?.reportingTo || '',
        noOfReporters: job?.noOfReporters || 0,
        teamSize: job?.teamSize || 0,
        hasPromotions:
          Array.isArray(job?.promotions) &&
            job.promotions.some(p => p.previousDesignation || p.promotionYr)
            ? 'YES'
            : 'NO',
        promotions: job?.promotions || [],
        currentRole: false,
      }));

      const careerGaps = updatedRoles.filter(role => role.isCareerGap).map(gap => ({
        startDate: gap?.startDate ? new Date(gap.startDate).toISOString() : null,
        endDate: gap?.endDate ? new Date(gap.endDate).toISOString() : null,
        reasons: gap?.reasons || '',
      }));

      const awards = candidateData?.workExperience?.awards || [];
      const techProficiencies = candidateData?.workExperience?.techProficiencies || [];
      const professionalMemberships = candidateData?.workExperience?.professionalMemberships || [];
      const totalYrs = candidateData?.workExperience?.totalYrs || 0;

      const panelPayload = {
        totalYrs,
        workExMonths: 0,
        currentRole,
        previousJobs,
        awards,
        hasCareerGaps: careerGaps.length > 0 ? 'YES' : 'NO',
        careerGaps,
        techProficiencies,
        professionalMemberships,
      }

      await CandidatePostLoginService.submitPanelByType('WORK_EXPERIENCE', panelPayload);

      const updatedCandidateData = await fetchCandidateData();
      const updatedJobRoles = [
        ...(updatedCandidateData?.workExperience?.currentRole ? [updatedCandidateData.workExperience.currentRole] : []),
        ...(updatedCandidateData?.workExperience?.previousJobs || []),
      ];

      const updatedCareerGaps = updatedCandidateData?.workExperience?.careerGaps?.map(gap => ({
        isCareerGap: true,
        ...gap
      })) || [];

      const combinedRoles = [...updatedJobRoles, ...updatedCareerGaps];

      setEditedJobRoles(combinedRoles);
      refreshPage();
      setShowEditModal(false);

    } catch (error) {
      console.error('Failed to save work experience:', error);
    }
  };


  const handleEdit = (index) => {
    const selected = editedJobRoles[index];
    setModalIndex(index);
    setModalFormData({ ...selected, previousJobs: [] });

    setModalMeta({
      isCareerGap: selected?.isCareerGap || false,
      isCurrent: index === 0 && !selected?.isCareerGap,
      isNew: !!selected?._isNew,

    });

    setShowEditModal(true);
  };

  const handleAddPreviousJob = () => {
    const newJob = {
      designation: '',
      orgName: '',
      location: '',
      startDate: '',
      endDate: '',
      industry: '',
      industrySubcategory: '',
      function: '',
      functionSubcategory: '',
      topThreeResponsibilities: '',
      topThreeAchievements: '',
      reportingTo: '',
      noOfReporters: 0,
      teamSize: 0,
      hasPromotions: 'NO',
      promotions: [],
      currentRole: false,
      _isNew: true
    };

    setModalFormData(newJob);
    setModalIndex(editedJobRoles.length);
    setModalMeta({ isCareerGap: false, isCurrent: false, isNew: true });

    setShowEditModal(true);
  };

  const handleAddCareerGap = () => {
    const newGap = {
      isCareerGap: true,
      startDate: '',
      endDate: '',
      reasons: '',
      _isNew: true
    };

    setModalFormData(newGap);

    setModalIndex(editedJobRoles.length);
    setModalMeta({ isCareerGap: false, isCurrent: false, isNew: true });

    setShowEditModal(true);
  };

  const extendedJobRoles = [...editedJobRoles];
  const visibleJobRoles = showAll ? extendedJobRoles : extendedJobRoles.slice(0, 4);

  const handleAddNewCurrentRoleClick = () => {
    const newCurrent = {
      designation: '',
      orgName: '',
      location: '',
      startDate: '',
      endDate: '',
      industry: '',
      industrySubcategory: '',
      function: '',
      functionSubcategory: '',
      topThreeResponsibilities: '',
      topThreeAchievements: '',
      reportingTo: '',
      noOfReporters: 0,
      teamSize: 0,
      hasPromotions: 'NO',
      promotions: [],
      currentRole: true,
      _isNew: true
    };

    setModalFormData(newCurrent);
    setModalIndex(0);
    setModalMeta({ isCareerGap: false, isCurrent: false, isNew: true });

    setShowEditModal(true);
  };

  const getCareerGapNumber = (index, isNew = false) =>
    editedJobRoles.filter((r, i) => r.isCareerGap && i <= index).length + (isNew ? 1 : 0);

  const getPreviousJobNumber = (index, isNew = false) =>
    editedJobRoles.filter((r, i) => !r.isCareerGap && i <= index && i !== 0).length + (isNew ? 1 : 0);

  useEffect(() => {
    if (!modalFormData) return;

    if (!modalFormData.isCareerGap && modalFormData.industry) {
      const match = industrySectors.find(s => (s.industrySector || '').toLowerCase() === (modalFormData.industry || '').toLowerCase());
      if (match?.id) {
        fetchSubcategoriesForKey(match.id, 'current');
      } else {
        setModalSubcategoriesMap(prev => ({ ...prev, current: [] }));
      }
    } else {
      setModalSubcategoriesMap(prev => ({ ...prev, current: [] }));
    }

    if (Array.isArray(modalFormData.previousJobs)) {
      modalFormData.previousJobs.forEach((pj, idx) => {
        const key = `prev-${idx}`;
        if (pj && pj.industry) {
          const match = industrySectors.find(s => (s.industrySector || '').toLowerCase() === (pj.industry || '').toLowerCase());
          if (match?.id) {
            fetchSubcategoriesForKey(match.id, key);
          } else {
            setModalSubcategoriesMap(prev => ({ ...prev, [key]: [] }));
          }
        } else {
          setModalSubcategoriesMap(prev => ({ ...prev, [key]: [] }));
        }
      });
    }

    if (!modalFormData.isCareerGap && modalFormData.function) {
      const matchF = functions.find(f => (f.function || '').toLowerCase() === (modalFormData.function || '').toLowerCase());
      if (matchF?.id) {
        fetchFunctionSubcategoriesForKey(matchF.id, 'current-func');
      } else {
        setModalFunctionSubcategoriesMap(prev => ({ ...prev, 'current-func': [] }));
      }
    } else {
      setModalFunctionSubcategoriesMap(prev => ({ ...prev, 'current-func': [] }));
    }

    if (Array.isArray(modalFormData.previousJobs)) {
      modalFormData.previousJobs.forEach((pj, idx) => {
        const key = `prev-func-${idx}`;
        if (pj && pj.function) {
          const match = functions.find(f => (f.function || '').toLowerCase() === (pj.function || '').toLowerCase());
          if (match?.id) {
            fetchFunctionSubcategoriesForKey(match.id, key);
          } else {
            setModalFunctionSubcategoriesMap(prev => ({ ...prev, [key]: [] }));
          }
        } else {
          setModalFunctionSubcategoriesMap(prev => ({ ...prev, [key]: [] }));
        }
      });
    }
  }, [modalFormData, industrySectors, functions]);

  const getIndustryLabelById = (id) => {
    const found = industrySectors.find(s => s.id == id);
    return found?.industrySector || '';
  };
  const getFunctionLabelById = (id) => {
    const found = functions.find(f => f.id == id);
    return found?.function || '';
  };
  const handleModalIndustrySelect = async (industryId) => {
    const label = getIndustryLabelById(industryId);
    setModalFormData(prev => ({
      ...prev,
      industry: label,
      industrySubcategory: ''
    }));
    if (industryId) await fetchSubcategoriesForKey(industryId, 'current');
  };

  const handleModalSubcategorySelect = (label) => {
    setModalFormData(prev => ({ ...prev, industrySubcategory: label }));
  };

  const handlePrevJobIndustrySelect = async (idx, industryId) => {
    const label = getIndustryLabelById(industryId);
    const updated = [...(modalFormData.previousJobs || [])];
    updated[idx] = { ...updated[idx], industry: label, industrySubcategory: '' };
    setModalFormData(prev => ({ ...prev, previousJobs: updated }));
    if (industryId) await fetchSubcategoriesForKey(industryId, `prev-${idx}`);
  };

  const handlePrevJobSubcategorySelect = (idx, label) => {
    const updated = [...(modalFormData.previousJobs || [])];
    updated[idx] = { ...updated[idx], industrySubcategory: label };
    setModalFormData(prev => ({ ...prev, previousJobs: updated }));
  };

  const handleModalFunctionSelect = async (functionId) => {
    const label = getFunctionLabelById(functionId);
    setModalFormData(prev => ({
      ...prev,
      function: label,
      functionSubcategory: ''
    }));
    if (functionId) await fetchFunctionSubcategoriesForKey(functionId, 'current-func');
  };

  const handleModalFunctionSubcategorySelect = (label) => {
    setModalFormData(prev => ({ ...prev, functionSubcategory: label }));
  };

  const handlePrevJobFunctionSelect = async (idx, functionId) => {
    const label = getFunctionLabelById(functionId);
    const updated = [...(modalFormData.previousJobs || [])];
    updated[idx] = { ...updated[idx], function: label, functionSubcategory: '' };
    setModalFormData(prev => ({ ...prev, previousJobs: updated }));
    if (functionId) await fetchFunctionSubcategoriesForKey(functionId, `prev-func-${idx}`);
  };

  const handlePrevJobFunctionSubcategorySelect = (idx, label) => {
    const updated = [...(modalFormData.previousJobs || [])];
    updated[idx] = { ...updated[idx], functionSubcategory: label };
    setModalFormData(prev => ({ ...prev, previousJobs: updated }));
  };

  return (
    <Container className="pt-2 work-experience-work-container">
      {errorMessage && (
        <div className="custom-alert-overlay">
          <div className="custom-alert-box">
            <div className="custom-alert-content">
              <p>{errorMessage}</p>
              <button className="custom-alert-button" onClick={() => setErrorMessage('')}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      <Row>
        <Col>
          <Row className="align-items-center">
            <Col>
              <h3 className="candidate-heading d-inline-block">
                Work Experience ({extendedJobRoles.length})
                <img src={ExperienceIcon} alt="Experience" className="profile-work-experience-nav-icon" />
              </h3>
            </Col>
            <Col className="text-end">
              <div
                style={{ color: '#F35824', cursor: 'pointer', fontWeight: '500' }}
                onClick={() => setShowPopup(true)}
                className='candidate-detail-add-head'
              >
                <FontAwesomeIcon icon={faPlusCircle} /> Add Experience
              </div>
            </Col>
          </Row>

          <Modal show={showPopup} onHide={() => setShowPopup(false)} centered backdrop="static" className="candidate-custom-modal" dialogClassName="small-education-modal">
            <Modal.Header className="d-flex justify-content-between align-items-center">
              <Modal.Title className="w-100 text-center">Add Work Experience</Modal.Title>
              <img
                src={CloseIcon}
                alt="Close"
                onClick={() => {
                  setShowPopup(false);
                  setFieldErrors({});

                  setRemovedModals([]);
                }}
                style={{ cursor: 'pointer', width: '24px', height: '24px' }}
              />
            </Modal.Header>
            <Modal.Body>
              <div className="mb-2" style={{ color: '#F35824', cursor: 'pointer', fontWeight: '500' }} onClick={() => { handleAddPreviousJob(); setShowPopup(false); }}>
                <FontAwesomeIcon icon={faPlusCircle} /> Add Previous Job
              </div>
              <div className="mb-2" style={{ color: '#F35824', cursor: 'pointer', fontWeight: '500' }} onClick={() => { handleAddCareerGap(); setShowPopup(false); }}>
                <FontAwesomeIcon icon={faPlusCircle} /> Add Career Gap
              </div>
              <div className="mb-2" style={{ color: '#F35824', cursor: 'pointer', fontWeight: '500' }} onClick={() => { handleAddNewCurrentRoleClick(); setShowPopup(false); }}>
                <FontAwesomeIcon icon={faPlusCircle} /> Add New Current Role
              </div>
            </Modal.Body>
          </Modal>

          <Container>

            {visibleJobRoles.map((jobRole, index) => (
              <div key={index} style={{ marginBottom: '20px' }}
                className="d-flex justify-content-between align-items-start mb-3">
                <div style={{ flex: 1 }}>

                  <CandidateJobRoleDetails
                    jobDetail={jobRole}
                    index={index}
                    isCurrentRole={index === 0 && !jobRole.isCareerGap}
                    isEditing={editingIndexes.includes(index)}
                    onChange={handleFieldChange}
                  />
                </div>
                <div >
                  <button onClick={() => handleEdit(index)} className="candidate-profile-edit-button">
                    <FontAwesomeIcon icon={faPen} />
                  </button>

                </div>
              </div>
            ))}
          </Container>

          {extendedJobRoles.length > 4 && (
            <Row>
              <Col>
                <a
                  className="experience-view-all"
                  onClick={() => setShowAll((prev) => !prev)}
                >
                  {showAll ? 'View Less' : 'View All'}&nbsp;
                  <FontAwesomeIcon icon={showAll ? faChevronUp : faChevronDown} />
                </a>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered backdrop="static" className="candidate-custom-modal" dialogClassName="candidate-custom-modal-dialog">
        <Modal.Header className="d-flex justify-content-between align-items-center">

          <Modal.Title>
            {(modalFormData?.isCareerGap ?? modalMeta.isCareerGap)
              ? isEditMode ? 'Edit Career Gap' : 'Add Career Gap'
              : (modalIndex === 0 || modalMeta.isCurrent)
                ? isEditMode ? 'Edit Current Role' : 'Add Current Role'
                : isEditMode ? 'Edit Previous Job' : 'Add Previous Job'}
          </Modal.Title>

          <img
            src={CloseIcon}
            alt="Close"
            onClick={() => {
              setFieldErrors({});
              setShowEditModal(false)
            }}
            style={{
              cursor: 'pointer',
              width: '24px',
              height: '24px',
            }}
          />
        </Modal.Header>
        <Modal.Body>
          <style>
            {`
    .react-datepicker__input-container input {
      border: 1px solid #ced4da !important;
      box-shadow: none !important;
    }

    .react-datepicker__input-container input:focus {
      border: 1px solid #ced4da !important;
      box-shadow: none !important;
      outline: none;
    }
  `}
          </style>
          <h5 style={{ marginBottom: '20px' }}>
            {(modalFormData?.isCareerGap || modalMeta.isCareerGap)
              ? `Career Gap - ${getCareerGapNumber(modalIndex, modalIndex >= editedJobRoles.length)}`
              : (modalIndex === 0 || modalMeta.isCurrent)
                ? 'Current Role'
                : `Previous Job - ${getPreviousJobNumber(modalIndex, modalIndex >= editedJobRoles.length)}`}
          </h5>
          {!modalFormData && (
            <div className="alert alert-warning mb-3">
              This entry will be removed once you click <strong>Save</strong>.
            </div>
          )}


          {modalFormData && (
            <Form>
              {modalFormData.isCareerGap ? (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date <span style={{ color: 'red' }}>*</span></Form.Label>
                    <ReactDatePicker
                      selected={modalFormData.startDate ? new Date(modalFormData.startDate) : null}
                      onChange={(val) => {
                        const startDate = val ? val.toISOString() : '';
                        setModalFormData((prev) => ({ ...prev, startDate }));
                        setFieldErrors(prev => ({
                          ...prev,
                          startDate: validateWorkField('startDate', startDate, { endDate: modalFormData.endDate }),
                        }));
                      }}
                      showMonthYearPicker
                      dateFormat="MM/yyyy"
                      maxDate={new Date()}
                      autoComplete="off"
                      className={`form-control ${fieldErrors.startDate ? 'is-invalid' : ''}`}
                    />
                    {fieldErrors.startDate && <div className="invalid-feedback d-block">{fieldErrors.startDate}</div>}

                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>End Date <span style={{ color: 'red' }}>*</span></Form.Label>
                    <ReactDatePicker
                      selected={modalFormData.endDate ? new Date(modalFormData.endDate) : null}
                      onChange={(val) => {
                        const endDate = val ? val.toISOString() : '';
                        setModalFormData((prev) => ({ ...prev, endDate }));
                        setFieldErrors(prev => ({
                          ...prev,
                          endDate: validateWorkField('endDate', endDate, { startDate: modalFormData.startDate }),
                        }));
                      }}
                      showMonthYearPicker
                      dateFormat="MM/yyyy"
                      maxDate={new Date()}
                      autoComplete="off"
                      className={`form-control ${fieldErrors.endDate ? 'is-invalid' : ''}`}
                    />
                    {fieldErrors.endDate && <div className="invalid-feedback d-block">{fieldErrors.endDate}</div>}

                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Reason for Gap </Form.Label>
                    <Form.Control
                      style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                      as="textarea"
                      spellCheck='false'
                      rows={3}
                      value={modalFormData.reasons || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalFormData((prev) => ({ ...prev, reasons: val }));
                        setFieldErrors(prev => ({
                          ...prev,
                          reasons: validateWorkField('reasons', val),
                        }));
                      }}
                      className={`form-control ${fieldErrors.reasons ? 'is-invalid' : ''}`}
                    />
                    {fieldErrors.reasons && <div className="invalid-feedback">{fieldErrors.reasons}</div>}

                  </Form.Group>

                  {modalFormData?.isCareerGap && (
                    <div style={{ marginTop: '20px' }}>
                      {(modalFormData.careerGaps || []).map((gap, index) => (
                        <div key={index} className="border rounded p-3 mb-3">
                          <h5>Career Gap {index + 1}</h5>

                          <Form.Group className="mb-2">
                            <Form.Label>Start Date <span style={{ color: 'red' }}>*</span></Form.Label>
                            <ReactDatePicker
                              selected={gap.startDate ? new Date(gap.startDate) : null}
                              onChange={(val) => {
                                const updated = [...modalFormData.careerGaps];
                                updated[index].startDate = val ? val.toISOString() : '';
                                setModalFormData(prev => ({ ...prev, careerGaps: updated }));
                                setFieldErrors(prev => ({
                                  ...prev,
                                  [`gapStart-${index}`]: validateWorkField('startDate', updated[index].startDate, {
                                    endDate: updated[index].endDate,
                                  }),
                                }));
                              }}
                              showMonthYearPicker
                              dateFormat="MM/yyyy"
                              maxDate={new Date()}
                              autoComplete="off"
                              className={`form-control ${fieldErrors[`gapStart-${index}`] ? 'is-invalid' : ''}`}
                            />
                            {fieldErrors[`gapStart-${index}`] && (
                              <div className="invalid-feedback d-block">{fieldErrors[`gapStart-${index}`]}</div>
                            )}

                          </Form.Group>

                          <Form.Group className="mb-2">
                            <Form.Label>End Date <span style={{ color: 'red' }}>*</span></Form.Label>
                            <ReactDatePicker
                              selected={gap.endDate ? new Date(gap.endDate) : null}
                              onChange={(val) => {
                                const updated = [...modalFormData.careerGaps];
                                updated[index].endDate = val ? val.toISOString() : '';
                                setModalFormData(prev => ({ ...prev, careerGaps: updated }));
                                setFieldErrors(prev => ({
                                  ...prev,
                                  [`gapEnd-${index}`]: validateWorkField('endDate', updated[index].endDate, {
                                    startDate: updated[index].startDate,
                                  }),
                                }));
                              }}
                              showMonthYearPicker
                              dateFormat="MM/yyyy"
                              maxDate={new Date()}
                              autoComplete="off"
                              className={`form-control ${fieldErrors[`gapEnd-${index}`] ? 'is-invalid' : ''}`}
                            />
                            {fieldErrors[`gapEnd-${index}`] && (
                              <div className="invalid-feedback d-block">{fieldErrors[`gapEnd-${index}`]}</div>
                            )}

                          </Form.Group>

                          <Form.Group className="mb-2">
                            <Form.Label>Reason <span style={{ color: 'red' }}>*</span></Form.Label>
                            <Form.Control
                              as="textarea"
                              style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                              spellCheck='false'
                              rows={3}
                              value={gap.reasons || ''}
                              onChange={(e) => {
                                const updated = [...modalFormData.careerGaps];
                                updated[index].reasons = e.target.value;
                                setModalFormData(prev => ({ ...prev, careerGaps: updated }));
                                setFieldErrors(prev => ({
                                  ...prev,
                                  [`gapReason-${index}`]: validateWorkField('reasons', updated[index].reasons),
                                }));
                              }}
                              className={`form-control ${fieldErrors[`gapReason-${index}`] ? 'is-invalid' : ''}`}
                            />
                            {fieldErrors[`gapReason-${index}`] && (
                              <div className="invalid-feedback">{fieldErrors[`gapReason-${index}`]}</div>
                            )}

                          </Form.Group>
                        </div>
                      ))}
                    </div>
                  )}

                </>
              ) : (
                <>
                  <Form.Group>
                    <Form.Label>Designation<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Form.Control
                      style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                      type="text"
                      spellCheck='false'
                      value={modalFormData.designation || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalFormData((prev) => ({ ...prev, designation: val }));
                        setFieldErrors(prev => ({
                          ...prev,
                          designation: validateWorkField('designation', val),
                        }));
                      }}
                      className={`form-control ${fieldErrors.designation ? 'is-invalid' : ''}`}
                    />
                    {fieldErrors.designation && <div className="invalid-feedback">{fieldErrors.designation}</div>}
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Organization Name<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Form.Control
                      style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                      type="text"
                      spellCheck='false'
                      value={modalFormData.orgName || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalFormData((prev) => ({ ...prev, orgName: val }));
                        setFieldErrors(prev => ({
                          ...prev,
                          orgName: validateWorkField('orgName', val),
                        }));
                      }}
                      className={`form-control ${fieldErrors.orgName ? 'is-invalid' : ''}`}
                    />
                    {fieldErrors.orgName && <div className="invalid-feedback">{fieldErrors.orgName}</div>}
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Location</Form.Label>
                    <Form.Select
                      style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                      value={modalFormData.location || ''}
                      onChange={(e) =>
                        setModalFormData((prev) => ({ ...prev, location: e.target.value }))
                      }
                    >
                      <option value="">Select a city</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Start Date</Form.Label>
                    <ReactDatePicker
                      selected={modalFormData.startDate ? new Date(modalFormData.startDate) : null}
                      onChange={(val) => {
                        const startDate = val ? val.toISOString() : '';
                        setModalFormData((prev) => ({ ...prev, startDate }));
                        setFieldErrors(prev => ({
                          ...prev,
                          startDate: validateWorkField('startDate', startDate, { endDate: modalFormData.endDate }),
                        }));
                      }}
                      showMonthYearPicker
                      dateFormat="MM/yyyy"
                      className={`form-control ${fieldErrors.startDate ? 'is-invalid' : ''}`}
                      maxDate={new Date()}
                      autoComplete="off"
                    />
                    {fieldErrors.startDate && <div className="invalid-feedback d-block">{fieldErrors.startDate}</div>}
                  </Form.Group>
                  {modalIndex !== 0 && (
                    <Form.Group className="mb-3">
                      <Form.Label>End Date</Form.Label>
                      <ReactDatePicker
                        selected={modalFormData.endDate ? new Date(modalFormData.endDate) : null}
                        onChange={(val) => {
                          const endDate = val ? val.toISOString() : '';
                          setModalFormData((prev) => ({ ...prev, endDate }));
                          setFieldErrors(prev => ({
                            ...prev,
                            endDate: validateWorkField('endDate', endDate, { startDate: modalFormData.startDate }),
                          }));
                        }}
                        showMonthYearPicker
                        dateFormat="MM/yyyy"
                        className={`form-control ${fieldErrors.endDate ? 'is-invalid' : ''}`}
                        maxDate={new Date()}
                        autoComplete="off"
                      />
                      {fieldErrors.endDate && <div className="invalid-feedback d-block">{fieldErrors.endDate}</div>}
                    </Form.Group>
                  )}
                  {modalIndex == 0 && (

                    <Form.Group>
                      <Form.Label>Notice Period</Form.Label>
                      <Form.Control
                        style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                        type="number"
                        spellCheck='false'
                        value={modalFormData.noticePeriod || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setModalFormData((prev) => ({ ...prev, noticePeriod: val }));
                          // setFieldErrors(prev => ({
                          //   ...prev,
                          //   noticePeriod: validateWorkField('noticePeriod', val),
                          // }));
                        }}
                      // className={`form-control ${fieldErrors.noticePeriod ? 'is-invalid' : ''}`}
                      />
                      {/* {fieldErrors.noticePeriod && <div className="invalid-feedback">{fieldErrors.noticePeriod}</div>} */}
                    </Form.Group>
                  )}
                  <Form.Group>
                    <Form.Label>Industry<span style={{ color: 'red' }}>*</span></Form.Label>

                    <Form.Select
                      style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                      value={
                        (industrySectors.find(s => (s.industrySector || '').toLowerCase() === (modalFormData.industry || '').toLowerCase())?.id) ?? ''
                      }
                      onChange={(e) => handleModalIndustrySelect(e.target.value)}
                      className={`form-control ${fieldErrors.industry ? 'is-invalid' : ''}`}
                    >
                      <option value="">Select industry</option>
                      {industrySectors.map((s) => (
                        <option key={s.id ?? s.industrySector} value={s.id ?? ''}>
                          {s.industrySector}
                        </option>
                      ))}
                    </Form.Select>
                    {fieldErrors.industry && <div className="invalid-feedback">{fieldErrors.industry}</div>}
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Industry Subcategory</Form.Label>
                    <Form.Select
                      style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                      value={modalFormData.industrySubcategory || ''}
                      onChange={(e) => handleModalSubcategorySelect(e.target.value)}
                      disabled={!modalSubcategoriesMap.current || (modalSubcategoriesMap.current || []).length === 0}
                    >
                      <option value="">Select subcategory</option>
                      {(modalSubcategoriesMap.current || []).map((sub) => (
                        <option key={sub.id} value={sub.subcategory || sub.industrySubcategory || sub.industrySubCategory || ''}>
                          {sub.subcategory || sub.industrySubcategory || sub.industrySubCategory || ''}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Function</Form.Label>
                    <Form.Select
                      style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                      value={
                        (functions.find(f => (f.function || '').toLowerCase() === (modalFormData.function || '').toLowerCase())?.id) ?? ''
                      }
                      onChange={(e) => handleModalFunctionSelect(e.target.value)}
                    >
                      <option value="">Select function</option>
                      {functions.map((fn) => (
                        <option key={fn.id ?? fn.function} value={fn.id ?? ''}>
                          {fn.function}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Function Subcategory</Form.Label>
                    <Form.Select
                      style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                      value={modalFormData.functionSubcategory || ''}
                      onChange={(e) => handleModalFunctionSubcategorySelect(e.target.value)}
                      disabled={!((modalFunctionSubcategoriesMap['current-func'] || []).length)}
                    >
                      <option value="">Select function subcategory</option>
                      {(modalFunctionSubcategoriesMap['current-func'] || []).map((sub) => (
                        <option key={sub.id} value={sub.subcategory || ''}>
                          {sub.subcategory || ''}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Top 3 Responsibilities</Form.Label>
                    <Form.Control
                      style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                      as="textarea"
                      spellCheck='false'
                      rows={3}
                      value={modalFormData.topThreeResponsibilities || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalFormData((prev) => ({ ...prev, topThreeResponsibilities: val }));
                        setFieldErrors(prev => ({
                          ...prev,
                          topThreeResponsibilities: validateWorkField('topThreeResponsibilities', val),
                        }));
                      }}
                      className={`form-control ${fieldErrors.topThreeResponsibilities ? 'is-invalid' : ''}`}
                    />
                    {fieldErrors.topThreeResponsibilities && <div className="invalid-feedback">{fieldErrors.topThreeResponsibilities}</div>}
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Top 3 Achievements</Form.Label>
                    <Form.Control
                      style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                      as="textarea"
                      spellCheck='false'
                      rows={3}
                      value={modalFormData.topThreeAchievements || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalFormData((prev) => ({ ...prev, topThreeAchievements: val }));
                        setFieldErrors(prev => ({
                          ...prev,
                          topThreeAchievements: validateWorkField('topThreeAchievements', val),
                        }));
                      }}
                      className={`form-control ${fieldErrors.topThreeAchievements ? 'is-invalid' : ''}`}
                    />
                    {fieldErrors.topThreeAchievements && <div className="invalid-feedback">{fieldErrors.topThreeAchievements}</div>}
                  </Form.Group>
                  {!modalFormData.isCareerGap && (
                    <>
                      <Form.Group>
                        <Form.Check
                          type="checkbox"
                          label="Has Promotions"
                          checked={modalFormData.hasPromotions === 'YES'}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setModalFormData((prev) => ({
                              ...prev,
                              hasPromotions: checked ? 'YES' : 'NO',
                              promotions: checked ? prev.promotions?.length ? prev.promotions : [{}] : [],
                            }));
                          }}
                        />
                      </Form.Group>

                      {modalFormData.hasPromotions === 'YES' && (
                        <>
                          <Form.Label>Promotions</Form.Label>
                          {(modalFormData.promotions || []).map((promo, idx) => (
                            <div key={`promo-${idx}`} className="mb-3 border rounded p-2">
                              <Row className="align-items-end">
                                <Col>
                                  <Form.Group>
                                    <Form.Label>Previous Designation<span style={{ color: 'red' }}>*</span></Form.Label>
                                    <Form.Control
                                      style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                                      value={promo.previousDesignation || ''}
                                      spellCheck='false'
                                      onChange={(e) => {
                                        const updatedPromos = [...modalFormData.promotions];
                                        const val = e.target.value;
                                        updatedPromos[idx] = {
                                          ...updatedPromos[idx],
                                          previousDesignation: val,
                                        };
                                        setModalFormData((prev) => ({ ...prev, promotions: updatedPromos }));
                                        setFieldErrors(prev => ({
                                          ...prev,
                                          [`promotion-${idx}-previousDesignation`]: validateWorkField('previousDesignation', val),
                                        }));
                                      }}
                                      className={`form-control ${fieldErrors[`promotion-${idx}-previousDesignation`] ? 'is-invalid' : ''}`}
                                    />
                                    {fieldErrors[`promotion-${idx}-previousDesignation`] && (
                                      <div className="invalid-feedback">{fieldErrors[`promotion-${idx}-previousDesignation`]}</div>
                                    )}

                                  </Form.Group>
                                </Col>
                                <Col>
                                  <Form.Group>
                                    <Form.Label>Promotion Year<span style={{ color: 'red' }}>*</span></Form.Label>
                                    <Form.Control
                                      style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                                      type="text"
                                      spellCheck='false'
                                      value={promo.promotionYr || ''}
                                      onChange={(e) => {
                                        const updatedPromos = [...modalFormData.promotions];
                                        const val = e.target.value;
                                        updatedPromos[idx] = {
                                          ...updatedPromos[idx],
                                          promotionYr: val,
                                        };
                                        setModalFormData((prev) => ({ ...prev, promotions: updatedPromos }));
                                        setFieldErrors(prev => ({
                                          ...prev,
                                          [`promotion-${idx}-promotionYr`]: validateWorkField('promotionYr', val),
                                        }));
                                      }}
                                      className={`form-control ${fieldErrors[`promotion-${idx}-promotionYr`] ? 'is-invalid' : ''}`}
                                    />
                                    {fieldErrors[`promotion-${idx}-promotionYr`] && (
                                      <div className="invalid-feedback">{fieldErrors[`promotion-${idx}-promotionYr`]}</div>
                                    )}
                                  </Form.Group>
                                </Col>
                                <Col xs="auto">
                                  <div
                                    style={{ color: '#F35824', cursor: 'pointer', fontWeight: '500' }}
                                    onClick={() => {
                                      const filteredPromos = modalFormData.promotions.filter((_, i) => i !== idx);
                                      setModalFormData((prev) => ({
                                        ...prev,
                                        promotions: filteredPromos,
                                        hasPromotions: filteredPromos.length > 0 ? 'YES' : 'NO',
                                      }));
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faMinusCircle} />
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          ))}
                          <div
                            style={{ color: '#F35824', cursor: 'pointer', fontWeight: '500' }}
                            size="sm"
                            onClick={() =>
                              setModalFormData((prev) => ({
                                ...prev,
                                promotions: [...(prev.promotions || []), { previousDesignation: '', promotionYr: '' }],
                              }))
                            }
                          >
                            <FontAwesomeIcon icon={faPlusCircle} /> Add Promotion
                          </div>
                        </>
                      )}
                    </>
                  )}
                  {modalIndex !== 0 && !modalFormData?.isCareerGap && (
                    <div style={{ marginTop: '20px' }}>
                      {(() => {
                        const previousJobs = Array.isArray(modalFormData.previousJobs) ? modalFormData.previousJobs : [];
                        const baseIndex = modalIndex - previousJobs.length;
                        return previousJobs.map((job, idx) => {
                          const key = `prev-${idx}`;
                          const subcats = modalSubcategoriesMap[key] || [];
                          const funcKey = `prev-func-${idx}`;
                          const funcSubcats = modalFunctionSubcategoriesMap[funcKey] || [];
                          return (
                            <div key={idx} className="border rounded p-3 mb-4">
                              <h5>Previous Job - {baseIndex + idx + 1}</h5>

                              <Form.Group>
                                <Form.Label>Designation<span style={{ color: 'red' }}>*</span></Form.Label>
                                <Form.Control
                                  style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                                  value={job.designation || ''}
                                  spellCheck='false'
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const updated = [...modalFormData.previousJobs];
                                    updated[idx].designation = val;
                                    setModalFormData(prev => ({ ...prev, previousJobs: updated }));
                                    setFieldErrors(prev => ({
                                      ...prev,
                                      [`previousJob-${idx}-designation`]: validateWorkField('designation', val),
                                    }));
                                  }}
                                  className={`form-control ${fieldErrors[`previousJob-${idx}-designation`] ? 'is-invalid' : ''}`}
                                />
                                {fieldErrors[`previousJob-${idx}-designation`] && (
                                  <div className="invalid-feedback">{fieldErrors[`previousJob-${idx}-designation`]}</div>
                                )}

                              </Form.Group>

                              <Form.Group>
                                <Form.Label>Organization Name<span style={{ color: 'red' }}>*</span></Form.Label>
                                <Form.Control
                                  style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                                  value={job.orgName || ''}
                                  spellCheck='false'
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const updated = [...modalFormData.previousJobs];
                                    updated[idx].orgName = val;
                                    setModalFormData(prev => ({ ...prev, previousJobs: updated }));
                                    setFieldErrors(prev => ({
                                      ...prev,
                                      [`previousJob-${idx}-orgName`]: validateWorkField('orgName', val),
                                    }));
                                  }}
                                  className={`form-control ${fieldErrors[`previousJob-${idx}-orgName`] ? 'is-invalid' : ''}`}
                                />
                                {fieldErrors[`previousJob-${idx}-orgName`] && (
                                  <div className="invalid-feedback">{fieldErrors[`previousJob-${idx}-orgName`]}</div>
                                )}

                              </Form.Group>

                              <Form.Group>

                                <Form.Label>Location</Form.Label>
                                <Form.Select
                                  style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                                  value={job.location || ''}
                                  onChange={(e) => {
                                    const updated = [...modalFormData.previousJobs];
                                    updated[idx].location = e.target.value;
                                    setModalFormData(prev => ({ ...prev, previousJobs: updated }));
                                  }}
                                >
                                  <option value="">Select a city</option>
                                  {cities.map((city) => (
                                    <option key={city} value={city}>{city}</option>
                                  ))}
                                </Form.Select>
                              </Form.Group>

                              <Form.Group className="mb-3">
                                <Form.Label>Start Date</Form.Label>
                                <ReactDatePicker
                                  selected={job.startDate ? new Date(job.startDate) : null}
                                  onChange={(val) => {
                                    const startDate = val ? val.toISOString() : '';
                                    const updated = [...modalFormData.previousJobs];
                                    updated[idx].startDate = startDate;
                                    setModalFormData(prev => ({ ...prev, previousJobs: updated }));
                                    setFieldErrors(prev => ({
                                      ...prev,
                                      [`previousJob-${idx}-startDate`]: validateWorkField('startDate', startDate, { endDate: job.endDate }),
                                    }));
                                  }}
                                  showMonthYearPicker
                                  dateFormat="MM/yyyy"
                                  className={`form-control ${fieldErrors[`previousJob-${idx}-startDate`] ? 'is-invalid' : ''}`}
                                  maxDate={new Date()}
                                  autoComplete="off"
                                />
                                {fieldErrors[`previousJob-${idx}-startDate`] && (
                                  <div className="invalid-feedback d-block">{fieldErrors[`previousJob-${idx}-startDate`]}</div>
                                )}
                              </Form.Group>

                              <Form.Group className="mb-3">
                                <Form.Label>End Date</Form.Label>
                                <ReactDatePicker
                                  selected={job.endDate ? new Date(job.endDate) : null}
                                  onChange={(val) => {
                                    const endDate = val ? val.toISOString() : '';
                                    const updated = [...modalFormData.previousJobs];
                                    updated[idx].endDate = endDate;
                                    setModalFormData(prev => ({ ...prev, previousJobs: updated }));
                                    setFieldErrors(prev => ({
                                      ...prev,
                                      [`previousJob-${idx}-endDate`]: validateWorkField('endDate', endDate, { startDate: job.startDate }),
                                    }));
                                  }}
                                  showMonthYearPicker
                                  dateFormat="MM/yyyy"
                                  className={`form-control ${fieldErrors[`previousJob-${idx}-endDate`] ? 'is-invalid' : ''}`}
                                  maxDate={new Date()}
                                  autoComplete="off"
                                />
                                {fieldErrors[`previousJob-${idx}-endDate`] && (
                                  <div className="invalid-feedback d-block">{fieldErrors[`previousJob-${idx}-endDate`]}</div>
                                )}
                              </Form.Group>

                              <Form.Group>
                                <Form.Label>Industry <span style={{ color: 'red' }}>*</span></Form.Label>
                                <Form.Select
                                  style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                                  value={(industrySectors.find(s => (s.industrySector || '').toLowerCase() === (job.industry || '').toLowerCase())?.id) ?? ''}
                                  onChange={(e) => handlePrevJobIndustrySelect(idx, e.target.value)}
                                  className={`form-control ${fieldErrors[`previousJob-${idx}-industry`] ? 'is-invalid' : ''}`}
                                >
                                  <option value="">Select industry</option>
                                  {industrySectors.map((s) => (
                                    <option key={s.id ?? s.industrySector} value={s.id ?? ''}>
                                      {s.industrySector}
                                    </option>
                                  ))}
                                </Form.Select>
                                {fieldErrors[`previousJob-${idx}-industry`] && (
                                  <div className="invalid-feedback">{fieldErrors[`previousJob-${idx}-industry`]}</div>
                                )}
                              </Form.Group>

                              <Form.Group>
                                <Form.Label>Industry Subcategory</Form.Label>
                                <Form.Select
                                  style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                                  value={job.industrySubcategory || ''}
                                  onChange={(e) => handlePrevJobSubcategorySelect(idx, e.target.value)}
                                  disabled={!subcats || subcats.length === 0}
                                >
                                  <option value="">Select subcategory</option>
                                  {(subcats || []).map((s) => (
                                    <option key={s.id} value={s.subcategory || s.industrySubcategory || s.industrySubCategory || ''}>
                                      {s.subcategory || s.industrySubcategory || s.industrySubCategory || ''}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>

                              <Form.Group>
                                <Form.Label>Function</Form.Label>
                                <Form.Select
                                  style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                                  value={(functions.find(f => (f.function || '').toLowerCase() === (job.function || '').toLowerCase())?.id) ?? ''}
                                  onChange={(e) => handlePrevJobFunctionSelect(idx, e.target.value)}
                                >
                                  <option value="">Select Function</option>
                                  {functions.map((fn) => (
                                    <option key={fn.id ?? fn.function} value={fn.id ?? ''}>
                                      {fn.function}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>

                              <Form.Group>
                                <Form.Label>Function Subcategory</Form.Label>
                                <Form.Select
                                  style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                                  value={job.functionSubcategory || ''}
                                  onChange={(e) => handlePrevJobFunctionSubcategorySelect(idx, e.target.value)}
                                  disabled={!funcSubcats || funcSubcats.length === 0}
                                >
                                  <option value="">Select function subcategory</option>
                                  {(funcSubcats || []).map((s) => (
                                    <option key={s.id} value={s.subcategory || ''}>
                                      {s.subcategory || ''}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>

                              <Form.Group>
                                <Form.Label>Top 3 Responsibilities</Form.Label>
                                <Form.Control
                                  style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                                  value={job.topThreeResponsibilities || ''}
                                  spellCheck='false'
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const updated = [...modalFormData.previousJobs];
                                    updated[idx].topThreeResponsibilities = val;
                                    setModalFormData(prev => ({ ...prev, previousJobs: updated }));
                                    setFieldErrors(prev => ({
                                      ...prev,
                                      [`previousJob-${idx}-topThreeResponsibilities`]: validateWorkField('topThreeResponsibilities', val),
                                    }));
                                  }}
                                  className={`form-control ${fieldErrors[`previousJob-${idx}-topThreeResponsibilities`] ? 'is-invalid' : ''}`}
                                />
                                {fieldErrors[`previousJob-${idx}-topThreeResponsibilities`] && (
                                  <div className="invalid-feedback">{fieldErrors[`previousJob-${idx}-topThreeResponsibilities`]}</div>
                                )}

                              </Form.Group>

                              <Form.Group>
                                <Form.Label>Top 3 Achievements</Form.Label>
                                <Form.Control
                                  style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                                  value={job.topThreeAchievements || ''}
                                  spellCheck='false'
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const updated = [...modalFormData.previousJobs];
                                    updated[idx].topThreeAchievements = val;
                                    setModalFormData(prev => ({ ...prev, previousJobs: updated }));
                                    setFieldErrors(prev => ({
                                      ...prev,
                                      [`previousJob-${idx}-topThreeAchievements`]: validateWorkField('topThreeAchievements', val),
                                    }));
                                  }}
                                  className={`form-control ${fieldErrors[`previousJob-${idx}-topThreeAchievements`] ? 'is-invalid' : ''}`}
                                />
                                {fieldErrors[`previousJob-${idx}-topThreeAchievements`] && (
                                  <div className="invalid-feedback">{fieldErrors[`previousJob-${idx}-topThreeAchievements`]}</div>
                                )}

                              </Form.Group>

                              <Form.Check
                                type="checkbox"
                                label="Has Promotions"
                                checked={job.hasPromotions === 'YES'}
                                onChange={(e) => {
                                  const updated = [...modalFormData.previousJobs];
                                  const checked = e.target.checked;
                                  updated[idx].hasPromotions = checked ? 'YES' : 'NO';
                                  updated[idx].promotions = checked ? (updated[idx].promotions?.length ? updated[idx].promotions : [{}]) : [];
                                  setModalFormData(prev => ({ ...prev, previousJobs: updated }));
                                }}
                              />

                              {job.hasPromotions === 'YES' && (job.promotions || []).map((promo, pidx) => (
                                <Row key={`promo-${pidx}`} className="align-items-end mb-2">
                                  <Col>
                                    <Form.Group>
                                      <Form.Label>Previous Designation <span style={{ color: 'red' }}>*</span><span style={{ color: 'red' }}>*</span></Form.Label>
                                      <Form.Control
                                        style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                                        value={promo.previousDesignation || ''}
                                        spellCheck='false'
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          const updated = [...modalFormData.previousJobs];
                                          updated[idx].promotions[pidx].previousDesignation = val;
                                          setModalFormData(prev => ({ ...prev, previousJobs: updated }));
                                          setFieldErrors(prev => ({
                                            ...prev,
                                            [`previousJob-${idx}-promotion-${pidx}-previousDesignation`]: validateWorkField('previousDesignation', val),
                                          }));
                                        }}
                                        className={`form-control ${fieldErrors[`previousJob-${idx}-promotion-${pidx}-previousDesignation`] ? 'is-invalid' : ''}`}
                                      />
                                      {fieldErrors[`previousJob-${idx}-promotion-${pidx}-previousDesignation`] && (
                                        <div className="invalid-feedback">{fieldErrors[`previousJob-${idx}-promotion-${pidx}-previousDesignation`]}</div>
                                      )}
                                    </Form.Group>
                                  </Col>
                                  <Col>
                                    <Form.Group>
                                      <Form.Label>Promotion Year <span style={{ color: 'red' }}>*</span><span style={{ color: 'red' }}>*</span></Form.Label>
                                      <Form.Control
                                        style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                                        value={promo.promotionYr || ''}
                                        spellCheck='false'
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          const updated = [...modalFormData.previousJobs];
                                          updated[idx].promotions[pidx].promotionYr = val;
                                          setModalFormData(prev => ({ ...prev, previousJobs: updated }));
                                          setFieldErrors(prev => ({
                                            ...prev,
                                            [`previousJob-${idx}-promotion-${pidx}-promotionYr`]: validateWorkField('promotionYr', val),
                                          }));
                                        }}
                                        className={`form-control ${fieldErrors[`previousJob-${idx}-promotion-${pidx}-promotionYr`] ? 'is-invalid' : ''}`}
                                      />
                                      {fieldErrors[`previousJob-${idx}-promotion-${pidx}-promotionYr`] && (
                                        <div className="invalid-feedback">{fieldErrors[`previousJob-${idx}-promotion-${pidx}-promotionYr`]}</div>
                                      )}
                                    </Form.Group>
                                  </Col>
                                  <Col xs="auto">
                                    <div
                                      style={{ color: '#F35824', cursor: 'pointer', fontWeight: '500' }}
                                      onClick={() => {
                                        const updated = [...modalFormData.previousJobs];
                                        updated[idx].promotions = updated[idx].promotions.filter((_, i) => i !== pidx);
                                        updated[idx].hasPromotions = updated[idx].promotions.length ? 'YES' : 'NO';
                                        setModalFormData(prev => ({ ...prev, previousJobs: updated }));
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faMinusCircle} />
                                    </div>
                                  </Col>
                                </Row>
                              ))}

                              <div
                                style={{ color: '#F35824', cursor: 'pointer', fontWeight: '500' }}
                                onClick={() => {
                                  const updated = [...modalFormData.previousJobs];
                                  updated[idx].promotions = [...(updated[idx].promotions || []), { previousDesignation: '', promotionYr: '' }];
                                  setModalFormData(prev => ({ ...prev, previousJobs: updated }));
                                }}
                              >
                                <FontAwesomeIcon icon={faPlusCircle} /> Add Promotion
                              </div>

                              <div
                                style={{ color: '#F35824', cursor: 'pointer', fontWeight: '500', marginTop: '10px' }}
                                onClick={() => {
                                  const updated = modalFormData.previousJobs.filter((_, i) => i !== idx);
                                  setModalFormData(prev => ({ ...prev, previousJobs: updated }));
                                }}
                              >
                                <FontAwesomeIcon icon={faMinusCircle} /> Remove Job
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}

                </>
              )}
            </Form>

          )}

          {(isEditMode && modalIndex !== 0 && !removedModals.includes(modalIndex)) && (
            <div className="text-end mt-3">
              <div
                onClick={() => {
                  setRemovedModals(prev => [...prev, modalIndex]);
                  setModalFormData(null);
                }}
              >
                <FontAwesomeIcon icon={faMinusCircle} style={{ color: '#F35824' }} />
              </div>
            </div>
          )}

        </Modal.Body>
        <Modal.Footer>
          <button
            className="candidate-profile-edit-cancel"
            onClick={() => {
              setShowEditModal(false);
              setModalIndex(null);
              setModalFormData(null);
              setFieldErrors({});
              setRemovedModals([]);

            }}
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              if (modalIndex !== null) {
                setSavingJobRoles(true);
                try {
                  let updatedRoles = [...editedJobRoles];

                  if (modalFormData) {
                    let errors = [];

                    if (modalFormData.isCareerGap) {
                      errors = validateCareerGapForm(modalFormData);
                    } else {
                      const isCurrent = modalIndex === 0;
                      errors = validateJobForm(modalFormData, isCurrent);
                    }

                    if (errors.length > 0) {
                      setErrorMessage(`Please fill required field(s):\n- ${errors.join('\n- ')}`);
                      setSavingJobRoles(false);
                      return;
                    }

                    updatedRoles[modalIndex] = modalFormData;
                  } else {
                    updatedRoles.splice(modalIndex, 1);
                  }
                  const fieldHasErrors = Object.values(fieldErrors).some(error => error);
                  if (fieldHasErrors) {
                    setErrorMessage('Please fix the field-level validation errors before saving.');
                    setSavingJobRoles(false);
                    return;
                  }
                  await handleSave(updatedRoles);
                  setShowEditModal(false);
                  setModalIndex(null);
                  setModalFormData(null);
                  setRemovedModals([]);
                } catch (error) {
                  console.error('Error saving job roles', error);
                } finally {
                  setSavingJobRoles(false);
                }
              }
            }}

            className="candidate-profile-edit-save"
            disabled={savingJobRoles}
          >
            {savingJobRoles ? 'Saving...' : 'Save'}
          </button>
        </Modal.Footer>
      </Modal>
    </Container>

  );
};

export default CandidateWorkExperinceInfo;
